import { pantryColors } from '@pantry/ui'
import React, { useState } from 'react'
import {
  DEFAULT_LOYALTY_COUPON_ICON,
  LOYALTY_COUPON_ICON_CHOICES,
} from '../../../../types/loyaltyCoupon'

const p = pantryColors

// Custom icons pasted/typed by the merchant shouldn't be able to blow up the
// badge layout — cap to a handful of characters (covers multi-codepoint
// emoji + ZWJ sequences like "👨‍👩‍👧").
const MAX_CUSTOM_ICON_LENGTH = 8

interface Props {
  value: string
  onChange: (icon: string) => void
}

const swatchStyle = (active: boolean): React.CSSProperties => ({
  width: 34,
  height: 34,
  borderRadius: 8,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 17,
  background: active ? p.brand.light : p.gray[50],
  border: `1.5px solid ${active ? p.brand.primary : p.gray[200]}`,
  cursor: 'pointer',
  padding: 0,
})

/**
 * "Coupon Icon" field for the coupon modal — a curated row of emoji swatches
 * (category glyphs + the default ticket icon) plus a "+" button that reveals
 * a small text input for a custom emoji. Empty selection ("none picked")
 * means the ticket icon (`DEFAULT_LOYALTY_COUPON_ICON`) renders wherever the
 * coupon's icon is shown — this component never writes the fallback glyph
 * into `value` itself, only what the merchant explicitly picked.
 */
export const CouponIconPicker: React.FC<Props> = ({ value, onChange }) => {
  const isCustom = value !== '' && !LOYALTY_COUPON_ICON_CHOICES.includes(value)
  const [customOpen, setCustomOpen] = useState(isCustom)
  const [customDraft, setCustomDraft] = useState(isCustom ? value : '')

  const commitCustom = () => {
    const trimmed = customDraft.trim().slice(0, MAX_CUSTOM_ICON_LENGTH)
    onChange(trimmed)
  }

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        alignItems: 'center',
      }}
    >
      {LOYALTY_COUPON_ICON_CHOICES.map((icon) => (
        <button
          key={icon}
          type="button"
          aria-label={`Use ${icon} icon`}
          aria-pressed={!isCustom && value === icon}
          style={swatchStyle(!isCustom && value === icon)}
          onClick={() => {
            setCustomOpen(false)
            setCustomDraft('')
            onChange(icon)
          }}
        >
          {icon}
        </button>
      ))}
      <button
        type="button"
        aria-label="Use a custom icon"
        aria-pressed={isCustom}
        style={swatchStyle(isCustom)}
        onClick={() => setCustomOpen(true)}
      >
        {isCustom ? value : '＋'}
      </button>
      {customOpen && (
        <input
          autoFocus
          type="text"
          value={customDraft}
          placeholder="Paste an emoji"
          maxLength={MAX_CUSTOM_ICON_LENGTH}
          onChange={(e) => setCustomDraft(e.target.value)}
          onBlur={commitCustom}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              commitCustom()
            }
          }}
          style={{
            boxSizing: 'border-box',
            width: 96,
            height: 34,
            padding: '0 10px',
            border: `1.5px solid ${p.brand.primary}`,
            borderRadius: 8,
            fontSize: 15,
            textAlign: 'center',
            color: p.gray[800],
            background: p.white,
            outline: 'none',
          }}
        />
      )}
      <span style={{ fontSize: 11.5, color: p.gray[400] }}>
        {value ? '' : `Defaults to ${DEFAULT_LOYALTY_COUPON_ICON}`}
      </span>
    </div>
  )
}
